import { z } from 'zod'
import { calculateAge } from '@/lib/age'
import { inferSpanishDocumentType, validateSpanishDocument } from '@/lib/validators/dni.validator'
import { zodMessage } from '@/lib/schemas/validation-message'

/**
 * Schema per al formulari públic amb camps catalans
 * Basat en PublicGuestSchema però amb noms de camp catalans
 *
 * Els `message` són claus i18n (`zodMessage`), mai text: el formulari els tradueix en
 * pintar-los (`translateValidationMessage`). Criteris a `plans/guest-data-quality.md` §2.
 */

/** Document obligatori a partir d'aquesta edat (RD 933/2021) */
export const DOCUMENT_REQUIRED_FROM_AGE = 14
/** Parentesc obligatori per sota d'aquesta edat */
export const MINOR_UNDER_AGE = 18

/**
 * El formulari demana el document per a aquesta data de naixement? Mateix predicat per a
 * la UI (mostrar o amagar els camps) i per al schema (exigir-los): un camp amagat per edat
 * no pot donar error. Sense data encara, sí (els camps es veuen).
 */
export function requiresDocument(birthDate: string | null | undefined, today: Date = new Date()): boolean {
  const age = calculateAge(birthDate, today)
  return age === null || age >= DOCUMENT_REQUIRED_FROM_AGE
}

/** Menor de 18 (parentesc obligatori). Sense data, no. */
export function isMinor(birthDate: string | null | undefined, today: Date = new Date()): boolean {
  const age = calculateAge(birthDate, today)
  return age !== null && age < MINOR_UNDER_AGE
}

// Tipus de document oficials Mossos
export const TipusDocumentEnum = z.enum(['D', 'N', 'P', 'O'])
type TipusDocument = z.infer<typeof TipusDocumentEnum>

// Sexe oficial Mossos
export const SexeEnum = z.enum(['M', 'F', 'OTHER'], {
  errorMap: () => ({ message: zodMessage('form.validation.sexRequired') }),
})

/**
 * Valor per defecte del sexe al formulari (M0): «Altre». El schema el segueix exigint,
 * però amb aquest valor a les tres entrades del formulari ja no pot arribar buit.
 */
export const DEFAULT_SEXE: z.infer<typeof SexeEnum> = 'OTHER'

// Parentesc per menors
export const ParentescEnum = z.enum([
  'PM', 'TU', 'AB', 'BA', 'TI', 'HR', 'CY', 'HJ', 'NI', 'BN', 'SB', 'CD', 'SG', 'YN', 'OT'
])

const requiredString = (key: string) =>
  z.string({ required_error: zodMessage(key), invalid_type_error: zodMessage(key) }).min(1, zodMessage(key))

/**
 * Schema per un viatger individual (camps catalans)
 */
export const ViatgerSchema = z.object({
  // Identificació personal
  nom: requiredString('form.validation.firstNameRequired'),
  cognom1: requiredString('form.validation.lastName1Required'),
  cognom2: z.string().optional(),

  // Contacte
  email: z.union([
    z.string().email(zodMessage('form.validation.invalidEmail')),
    z.literal('').transform(() => undefined),
  ]).optional(),
  telefon: z.string().optional(),
  mobil: z.string().optional(),

  // Document: opcionals a l'objecte; obligatoris a partir de 14 anys (superRefine).
  // El tipus arrenca buit ('') a posta: no es pot suposar un DNI espanyol.
  tipus_document: z.union([TipusDocumentEnum, z.literal('')]).optional(),
  num_document: z.string().optional(),
  camp_suport: z.string().optional(), // IDESP per DNI/NIE
  data_caducitat_document: z.string().optional(), // Per Passaport, opcional (vegeu superRefine)

  // Dades personals
  sexe: SexeEnum.refine(val => !!val, { message: zodMessage('form.validation.sexRequired') }),
  data_naixement: requiredString('form.validation.birthDateRequired'),
  nacionalitat: z.string().length(3, zodMessage('form.validation.nationalityRequired')),

  // Adreça
  pais: z.string().max(2).optional(),
  ciutat: z.string().optional(),
  provincia: z.string().optional(),
  codi_postal: z.string().optional(),
  adreca: z.string().optional(),
  address_raw: z.string().optional(), // JSON de Google Maps

  // Parentesc (per menors) - transform empty string to undefined
  relacio_parentesc: z.preprocess(
    (val) => (val === '' ? undefined : val),
    ParentescEnum.optional()
  ),

  // Altres
  empresa: z.string().optional(),
  titol: z.string().optional(),
  peticions_especials: z.string().optional(),

  // Consentiments
  es_titular: z.boolean(),
  consentiment_rgpd: z.boolean().refine(val => val === true, {
    message: zodMessage('form.validation.gdprRequired'),
  }),
  consentiment_marketing: z.boolean(),

  // Firma
  signature: z.string().optional(),

  // IDs interns (per actualitzacions)
  _guestId: z.string().optional(),
  _originalIndex: z.number().optional(),
}).superRefine((data, ctx) => {
  // NOTA: camp_suport (IDESP) NO és obligatori segons RD 933/2021
  // El camp està desactivat al formulari (FormulariPublicPage.tsx, bloc IDESP comentat)

  // Validació creuada: telèfon O email obligatori (RD 933/2021)
  const hasTelefon = data.telefon && data.telefon.trim().length > 0
  const hasEmail = data.email && data.email.trim().length > 0
  if (!hasTelefon && !hasEmail) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: zodMessage('form.validation.phoneOrEmailRequired'),
      path: ['telefon'],
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: zodMessage('form.validation.phoneOrEmailRequired'),
      path: ['email'],
    })
  }

  // Validació condicional: relacio_parentesc obligatòria per menors de 18 anys (edat exacta)
  if (isMinor(data.data_naixement) && !data.relacio_parentesc) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: zodMessage('form.validation.parentescoRequired'),
      path: ['relacio_parentesc'],
    })
  }

  // Document: només a partir de 14 anys. Per sota, els camps ni es veuen ni es validen.
  if (!requiresDocument(data.data_naixement)) return

  const tipus = data.tipus_document || undefined
  const numero = data.num_document?.trim() || ''
  const inferit = numero ? inferSpanishDocumentType(numero) : null

  // Sense tipus: un DNI/NIE amb la lletra bona el diu ell mateix (el transform l'omple, M0).
  // Sense tipus i amb un número que no és DNI ni NIE (l'hoste estranger que no ha tocat el
  // desplegable): el missatge ja li diu què ha de triar. Altrament (sense número o amb la
  // lletra dolenta), es demana el tipus.
  if (!tipus && !(inferit && validateSpanishDocument(numero).isValid)) {
    const estranger = numero !== '' && !inferit
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: zodMessage(estranger ? 'form.validation.notSpanishFormat' : 'form.validation.documentTypeRequired'),
      path: ['tipus_document'],
    })
  }
  if (!numero) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: zodMessage('form.validation.documentRequired'),
      path: ['num_document'],
    })
  }

  // La caducitat del passaport és opcional (M0): no és a l'annex I del RD 933/2021, el V24
  // no l'envia (el camp 5 és la data d'expedició, sempre buit) i l'AEPD la considera dada
  // de més. El camp es queda i es desa si l'hoste l'escriu.

  // DNI/NIE: el format mana sobre el tipus (§2). Sense format espanyol → el document és
  // estranger: error al tipus i al número que diu de triar Passaport o Altres. Amb el
  // format de l'altre tipus i la lletra bona → cap error (el transform corregeix el tipus).
  // Amb el tipus buit i format espanyol, la lletra també es comprova.
  if ((tipus === 'D' || tipus === 'N') && numero && !inferit) {
    for (const path of ['tipus_document', 'num_document']) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: zodMessage('form.validation.notSpanishFormat'),
        path: [path],
      })
    }
  }
  if ((tipus === 'D' || tipus === 'N' || !tipus) && inferit) {
    const result = validateSpanishDocument(numero)
    if (result.code === 'LETTER') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: zodMessage('form.validation.checkLetter', { letter: result.expectedLetter ?? '' }),
        path: ['num_document'],
      })
    }
  }
}).transform((data) => {
  // Menor de 14: els camps de document estan amagats; un valor que hi hagués quedat d'abans
  // d'escriure la data de naixement no s'envia.
  if (!requiresDocument(data.data_naixement)) {
    return { ...data, tipus_document: undefined, num_document: undefined, data_caducitat_document: undefined }
  }

  // Tipus D/N o buit, i un número amb format de DNI/NIE i la lletra bona (el superRefine ja
  // ho ha comprovat): s'envia el tipus que diu el número. El formulari ja ho fa en viu
  // (onBlur). Passaport i Altres no es toquen mai.
  const tipus: TipusDocument | undefined = data.tipus_document || undefined
  const inferit = !tipus || tipus === 'D' || tipus === 'N' ? inferSpanishDocumentType(data.num_document) : null
  return { ...data, tipus_document: inferit ?? tipus }
})

export type Viatger = z.infer<typeof ViatgerSchema>

/**
 * Schema per al formulari complet (array de viatgers)
 */
export const PublicFormSchema = z.object({
  viatgers: z.array(ViatgerSchema).min(1, zodMessage('form.validation.required')),
})

/** Dades ja validades (el que rep `onSubmit`) */
export type PublicFormData = z.infer<typeof PublicFormSchema>
/** Valors dels camps del formulari (el que manega React Hook Form) */
export type PublicFormInput = z.input<typeof PublicFormSchema>
